import { useRef, useState } from "react";
import { FILE_ACCEPT, deleteFile, fileSize, fileUrl, uploadFile, type StoredFile } from "../filesApi";

/** Reconciles uploads made inside a draft editor (one that can be cancelled)
 * against what is actually kept, so no file is orphaned and none is deleted
 * while still referenced. Uploads happen the moment the user picks a file — long
 * before Save — so the two outcomes have to be settled explicitly:
 *   commit(keptIds) — deletes what the edit dropped (removed or replaced)
 *   discard()       — deletes everything uploaded during the edit
 * Both are best-effort: a failed cleanup leaves an unreferenced object, which is
 * harmless, and must never block saving. */
export function useDiscardedFiles(originalIds: string[]) {
  const original = useRef<string[]>(originalIds); // snapshot: what existed before the edit
  const uploaded = useRef<string[]>([]); // added during the edit

  return {
    /** Call whenever an attach control yields a new file. */
    track(f: StoredFile | undefined) {
      if (f) uploaded.current.push(f.id);
    },
    /** Saving: delete anything that existed or was uploaded but isn't kept. */
    commit(keptIds: string[]) {
      const kept = new Set(keptIds);
      for (const id of new Set([...original.current, ...uploaded.current])) {
        if (!kept.has(id)) deleteFile(id).catch(() => {});
      }
      original.current = keptIds;
      uploaded.current = [];
    },
    /** Cancelling: delete only what this edit uploaded — the originals stay,
     * because the references to them survive the cancel. */
    discard() {
      const keep = new Set(original.current);
      for (const id of uploaded.current) if (!keep.has(id)) deleteFile(id).catch(() => {});
      uploaded.current = [];
    },
  };
}

interface Props {
  file?: StoredFile;
  onChange: (f: StoredFile | undefined) => void;
  /** Button text when nothing is attached yet. */
  label: string;
  title?: string;
  /** Compact variant for a table/list row. */
  mini?: boolean;
  /** Don't delete the stored object when the file is removed or replaced — only
   * drop the reference. Set this inside a draft editor that can be cancelled:
   * deleting eagerly there would leave a dangling reference if the user then
   * cancels. The owner is then responsible for deleting discarded ids (see
   * useDiscardedFiles) once the edit is committed or abandoned. */
  deferDelete?: boolean;
}

/** Attach one stored file (PDF or image) — upload, view, replace, remove.
 * Used for the executed contract and for proof of payment. */
export default function FileAttach({ file, onChange, label, title, mini, deferDelete }: Props) {
  const ref = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  async function pick(f: File | undefined) {
    if (!f) return;
    setErr("");
    setBusy(true);
    try {
      const prev = file;
      const next = await uploadFile(f);
      onChange(next);
      // Replacing: drop the old object so R2 doesn't keep orphans. Best-effort —
      // the reference is already replaced, so a failed delete must not surface.
      if (prev && !deferDelete) deleteFile(prev.id).catch(() => {});
    } catch (e: any) {
      setErr(e?.message || "Upload failed.");
    } finally {
      setBusy(false);
      if (ref.current) ref.current.value = ""; // so re-picking the same file fires onChange
    }
  }

  async function remove() {
    if (!file) return;
    if (!confirm(`Remove ${file.name}? This deletes the uploaded file.`)) return;
    setErr("");
    if (deferDelete) {
      onChange(undefined); // the owner deletes it if and when the edit is saved
      return;
    }
    try {
      await deleteFile(file.id);
      onChange(undefined);
    } catch (e: any) {
      setErr(e?.message || "Could not remove the file.");
    }
  }

  return (
    <div className={mini ? "fattach fattach-mini" : "fattach"}>
      <input ref={ref} type="file" accept={FILE_ACCEPT} style={{ display: "none" }} onChange={(e) => pick(e.target.files?.[0])} />
      {file ? (
        <>
          <a className="fattach-file" href={fileUrl(file.id)} target="_blank" rel="noreferrer" title={`Open ${file.name}`}>
            📎 {file.name}
          </a>
          <span className="muted fattach-size">{fileSize(file.size)}</span>
          <button className="mini" onClick={() => ref.current?.click()} disabled={busy} title="Replace with a different file">
            {busy ? <span className="spinner" /> : "Replace"}
          </button>
          <button className="chip-x" onClick={remove} title="Remove this file">✕</button>
        </>
      ) : (
        <button className="mini" onClick={() => ref.current?.click()} disabled={busy} title={title}>
          {busy ? <span className="spinner" /> : `⤒ ${label}`}
        </button>
      )}
      {err && <span className="fattach-err">{err}</span>}
    </div>
  );
}
